import { motion } from 'motion/react';
import { X, LogOut, Mail, User, ShieldCheck, MailCheck } from 'lucide-react';
import { authService } from '../lib/firebase';
import { User as FirebaseUser } from 'firebase/auth';

interface UserPanelProps {
  user: FirebaseUser;
  isOpen: boolean;
  onClose: () => void;
}

export default function UserPanel({ user, isOpen, onClose }: UserPanelProps) {
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: isOpen ? 0 : '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed top-0 right-0 h-full w-full max-w-sm bg-white dark:bg-slate-900 border-l border-line dark:border-slate-800 shadow-2xl z-[110] p-8"
    >
      <div className="flex justify-between items-center mb-10">
        <h2 className="text-xl font-black dark:text-white flex items-center gap-2">
          <User className="w-5 h-5 text-accent" />
          My Profile
        </h2>
        <button 
          aria-label="Close Profile"
          onClick={onClose}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-400"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="flex flex-col items-center text-center mb-10">
        <div className="relative mb-4">
          <img 
            src={user.photoURL || `https://ui-avatars.com/api/?name=${user.displayName}`} 
            alt={user.displayName || 'User'} 
            className="w-24 h-24 rounded-3xl border-4 border-slate-50 dark:border-slate-800 shadow-lg"
          />
          <div className="absolute -bottom-2 -right-2 bg-emerald-500 text-white p-1.5 rounded-xl border-4 border-white dark:border-slate-900">
            <MailCheck className="w-3 h-3" />
          </div>
        </div>
        <h3 className="font-bold text-lg dark:text-white">{user.displayName}</h3>
        <p className="text-sm text-slate-500 mb-1">{user.email}</p>
        {user.emailVerified && (
          <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full">
            Verified Account
          </span>
        )}
      </div>

      <div className="space-y-4 mb-10">
        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-line dark:border-slate-800">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Security Info
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed italic">
            You are logged in via Google Authentication. Your data is protected by industry standard encryption.
          </p>
        </div>
      </div>

      <div className="absolute bottom-10 left-8 right-8 flex flex-col gap-3">
        <button
          onClick={() => {
            authService.signOut();
            onClose();
          }}
          className="w-full flex items-center justify-center gap-2 bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/10 dark:text-rose-400 py-4 rounded-2xl font-bold transition-all"
        >
          <LogOut className="w-5 h-5" /> Sign Out
        </button>
        <p className="text-[10px] text-center text-slate-400 uppercase tracking-widest font-medium">
          QuicklyUtils v1.0.0
        </p>
      </div>
    </motion.div>
  );
}
